import { Button } from '@panwds/react-ui';
import { SubmitButton as PanwSubmitButton } from '@panwds/react-form';
import { ReactNode } from 'react';
import styles from '../styles/Register.module.scss';

/**
 * Shared button components for subscription forms
 * These components provide consistent button styling across all subscription pages
 */

interface SubscriptionButtonProps {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    dataMetrics?: string;
    dataTestId?: string;
    type?: 'button' | 'submit' | 'reset';
    variant?: 'rounded' | 'standard';
}

/**
 * Primary subscription button with consistent branding
 * Uses the custom orange color and styling
 */
export const SubscriptionButton = ({
    children,
    onClick,
    disabled = false,
    dataMetrics,
    dataTestId,
    type = 'button',
    variant = 'rounded'
}: SubscriptionButtonProps) => {
    const buttonClass = variant === 'rounded'
        ? styles.customButton
        : styles.customButtonV2;

    return (
        <Button
            addClassName={buttonClass}
            size="lg"
            data-metrics={dataMetrics}
            data-testid={dataTestId || dataMetrics}
            type={type}
            onClick={onClick}
            disabled={disabled}
        >
            {children}
        </Button>
    );
};

interface SubscriptionSubmitButtonProps {
    children: ReactNode;
    disabled?: boolean;
    dataMetrics?: string;
    dataTestId?: string;
    variant?: 'rounded' | 'standard';
}

/**
 * Submit button for subscription forms
 * Uses the same styling as SubscriptionButton but for form submission
 */
export const SubscriptionSubmitButton = ({
    children,
    disabled = false,
    dataMetrics,
    dataTestId,
    variant = 'rounded'
}: SubscriptionSubmitButtonProps) => {
    const buttonClass = variant === 'rounded'
        ? styles.customButton
        : styles.customButtonV2;

    return (
        <PanwSubmitButton
            addClassName={buttonClass}
            disabled={disabled}
            dataMetrics={dataMetrics}
            dataTestId={dataTestId || dataMetrics}
        >
            {children}
        </PanwSubmitButton>
    );
};
